import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  InputAdornment,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { BORDER_RADIUS, COLORS } from "app/theme";
import {
  ActionStatus,
  ActionStatusIcon,
  actionStatusAccent,
  actionStatusLabel,
  actionStatusRank,
  normalizeActionStatus,
} from "common/components/actionStatus";
import DetailDialog from "common/components/DetailDialog";
import WarningIcon from "common/icons/WarningIcon";
import { getFormattedCategoryName } from "features/programmesActions/ActionList";
import type { AccessibleFarmerProgramme } from "model/useDashboardQueries";
import { useAccessibleFarmerProgrammes } from "model/useDashboardQueries";
import { useMemo, useState } from "react";
import type {
  ActionsSummaryData,
  FarmersByActionData,
} from "../hooks/useInsightsDashboardQueries";
import {
  useActionsSummary,
  useFarmersByAction,
} from "../hooks/useInsightsDashboardQueries";
import ActionDetailView from "./ActionDetailView";

const Card = styled(Box)({
  backgroundColor: "white",
  borderRadius: BORDER_RADIUS.medium,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  overflow: "hidden",
});

const OVERVIEW_GRID_COLS = "minmax(160px, 2.5fr) 0.8fr 0.9fr 0.8fr 0.9fr";
const FARMER_GRID_COLS = "minmax(120px, 2fr) 1fr 1fr";

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ─── Level 1: all actions farmers in the programme have taken ──────

type SelectedAction = { actionId: string; action: string };

function ActionsOverviewTable({
  onSelectAction,
}: {
  onSelectAction: (action: SelectedAction) => void;
}) {
  const { data, isLoading } = useActionsSummary();
  const rows = data?.data ?? [];

  return (
    <Card>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: OVERVIEW_GRID_COLS,
          gap: 1,
          px: 2,
          py: 1.5,
          borderBottom: "1px solid #f0f0f0",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a" }}
        >
          ACTION
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
        >
          FARMERS
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
        >
          NOT STARTED
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
        >
          ON TRACK
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
        >
          COMPLETED
        </Typography>
      </Box>

      {isLoading
        ? null
        : rows.map((row: ActionsSummaryData) => {
            const actionId = row["SubmittedActions.actionId"];
            const action = row["SubmittedActions.action"];
            const categoryId = parseInt(
              row["SubmittedActions.categoryId"] ?? "0",
              10
            );
            const category =
              getFormattedCategoryName(categoryId) ||
              row["SubmittedActions.category"];
            const total = Number(row["SubmittedActions.uniqueUserCount"]) || 0;
            const onTrack =
              Number(row["SubmittedActions.uniqueUsersOnTrack"]) || 0;
            const completed =
              Number(row["SubmittedActions.uniqueUsersCompleted"]) || 0;
            const notStarted = Math.max(total - onTrack - completed, 0);

            return (
              <Box
                key={actionId}
                onClick={() => onSelectAction({ actionId, action })}
                sx={{
                  display: "grid",
                  gridTemplateColumns: OVERVIEW_GRID_COLS,
                  gap: 1,
                  px: 2,
                  py: 1.5,
                  borderBottom: "1px solid #f4f4f5",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: COLORS.dahlia[50] },
                  "&:last-child": { borderBottom: "none" },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 500,
                      color: "#18181b",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {action}
                  </Typography>
                  {category && (
                    <Typography variant="caption" sx={{ color: "#a1a1aa" }}>
                      {category}
                    </Typography>
                  )}
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "#18181b", textAlign: "right" }}
                >
                  {total}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#3f3f46", textAlign: "right" }}
                >
                  {notStarted}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#0369a1", textAlign: "right" }}
                >
                  {onTrack}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "#15803d", textAlign: "right" }}
                >
                  {completed}
                </Typography>
              </Box>
            );
          })}

      {!isLoading && rows.length === 0 && (
        <Box sx={{ p: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No farmers in your programme have taken any actions yet.
          </Typography>
        </Box>
      )}
    </Card>
  );
}

// ─── Level 2: which farmers have taken a specific action ───────────

export type ActionFarmerRow = {
  farmerGuid: string;
  name: string;
  status: ActionStatus;
  submissionDate: string | null;
  // The chosen submission row itself, so a click can open ActionDetailView
  // without a second query.
  record: FarmersByActionData;
};

// A farmer can have more than one submission row for the same action (no DB
// uniqueness constraint on action_id + user_guid). We collapse to one row per
// farmer, keeping whichever submission is furthest along (completed beats
// on-track beats not-started) so the summary counts stay unambiguous.
export function buildActionFarmerRows(
  cubeRows: FarmersByActionData[] | undefined,
  roster: AccessibleFarmerProgramme[] | undefined
): ActionFarmerRow[] {
  const nameByGuid = new Map<string, string>();
  (roster ?? []).forEach((f) => {
    nameByGuid.set(f.userGuid, f.userName || f.userEmail);
  });

  const bestByGuid = new Map<string, FarmersByActionData>();
  (cubeRows ?? []).forEach((row) => {
    const guid = row["SubmittedActions.userGuid"];
    const existing = bestByGuid.get(guid);
    if (
      !existing ||
      actionStatusRank(normalizeActionStatus(row["SubmittedActions.status"])) <
        actionStatusRank(
          normalizeActionStatus(existing["SubmittedActions.status"])
        )
    ) {
      bestByGuid.set(guid, row);
    }
  });

  return Array.from(bestByGuid.entries()).map(([guid, row]) => ({
    farmerGuid: guid,
    name: nameByGuid.get(guid) || row["SubmittedActions.email"] || guid,
    status: normalizeActionStatus(row["SubmittedActions.status"]),
    submissionDate: row["SubmittedActions.submissionDate"] ?? null,
    record: row,
  }));
}

// An action marked on-track or completed should have evidence (a photo, file,
// or shared event) attached — flag it if it doesn't, so a corporate reviewer
// can catch status set ahead of the paperwork.
function hasMissingEvidence(row: ActionFarmerRow): boolean {
  if (row.status !== "on-track" && row.status !== "completed") return false;
  return (Number(row.record["SubmittedActions.evidenceCount"]) || 0) === 0;
}

function ActionFarmerBreakdown({
  actionId,
  actionTitle,
  onClose,
}: {
  actionId: string;
  actionTitle: string;
  onClose: () => void;
}) {
  const [search, setSearch] = useState("");
  const [selectedGuid, setSelectedGuid] = useState<string | null>(null);

  const { data: roster, isLoading: rosterLoading } =
    useAccessibleFarmerProgrammes();
  const { data: farmersData, isLoading: farmersLoading } =
    useFarmersByAction(actionId);

  const isLoading = rosterLoading || farmersLoading;

  const rows = useMemo(
    () => buildActionFarmerRows(farmersData?.data, roster),
    [farmersData?.data, roster]
  );

  const selectedRecord = farmersData?.data.find(
    (row) => row["SubmittedActions.guid"] === selectedGuid
  );
  const selectedFarmer = rows.find(
    (row) => row.farmerGuid === selectedRecord?.["SubmittedActions.userGuid"]
  );

  const filteredRows = useMemo(() => {
    if (!search.trim()) return rows;
    const term = search.toLowerCase();
    return rows.filter((r) => r.name.toLowerCase().includes(term));
  }, [rows, search]);

  const counts = useMemo(() => {
    const notStarted = filteredRows.filter(
      (r) => r.status === "submitted" || r.status === "other"
    ).length;
    const onTrack = filteredRows.filter((r) => r.status === "on-track").length;
    const completed = filteredRows.filter(
      (r) => r.status === "completed"
    ).length;
    return { notStarted, onTrack, completed };
  }, [filteredRows]);

  return (
    <DetailDialog
      open
      onClose={onClose}
      onBack={selectedRecord ? () => setSelectedGuid(null) : undefined}
      title={
        selectedRecord
          ? `${actionTitle} — ${
              selectedFarmer?.name ?? selectedRecord["SubmittedActions.email"]
            }`
          : actionTitle
      }
    >
      {selectedRecord ? (
        <ActionDetailView
          key={selectedRecord["SubmittedActions.guid"]}
          action={selectedRecord}
          farmerGuid={selectedRecord["SubmittedActions.userGuid"]}
        />
      ) : (
        <>
          <Box sx={{ mb: 2, display: "flex", gap: 2, flexWrap: "wrap" }}>
            <TextField
              size="small"
              placeholder="Search farmers..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#a1a1aa", fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                maxWidth: 360,
                "& .MuiOutlinedInput-root": {
                  borderRadius: `${BORDER_RADIUS.small}px`,
                  backgroundColor: "white",
                },
              }}
            />
          </Box>

          <Box sx={{ mb: 2, display: "flex", gap: 3 }}>
            <Typography variant="body2" sx={{ color: "#3f3f46" }}>
              <strong>{counts.notStarted}</strong> not started
            </Typography>
            <Typography variant="body2" sx={{ color: "#0369a1" }}>
              <strong>{counts.onTrack}</strong> on track
            </Typography>
            <Typography variant="body2" sx={{ color: "#15803d" }}>
              <strong>{counts.completed}</strong> completed
            </Typography>
          </Box>

          <Card>
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: FARMER_GRID_COLS,
                gap: 1,
                px: 2,
                py: 1.5,
                borderBottom: "1px solid #f0f0f0",
                backgroundColor: "#fafafa",
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#71717a" }}
              >
                FARMER
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
              >
                STATUS
              </Typography>
              <Typography
                variant="caption"
                sx={{ fontWeight: 600, color: "#71717a", textAlign: "right" }}
              >
                LAST SUBMITTED
              </Typography>
            </Box>

            {isLoading
              ? null
              : filteredRows.map((row) => {
                  const accent = actionStatusAccent(row.status);
                  const missingEvidence = hasMissingEvidence(row);
                  return (
                    <Box
                      key={row.farmerGuid}
                      onClick={() =>
                        setSelectedGuid(row.record["SubmittedActions.guid"])
                      }
                      sx={{
                        display: "grid",
                        gridTemplateColumns: FARMER_GRID_COLS,
                        gap: 1,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #f4f4f5",
                        borderLeft: accent ? `3px solid ${accent}` : undefined,
                        cursor: "pointer",
                        "&:hover": { backgroundColor: COLORS.dahlia[50] },
                        "&:last-child": { borderBottom: "none" },
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 500, color: "#18181b" }}
                      >
                        {row.name}
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        {missingEvidence && (
                          <Tooltip title="Missing evidence" arrow>
                            <WarningIcon
                              sx={{ fontSize: 16, color: "#d97706" }}
                            />
                          </Tooltip>
                        )}
                        <ActionStatusIcon status={row.status} />
                        <Typography variant="body2" sx={{ color: "#3f3f46" }}>
                          {actionStatusLabel(row.status)}
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ color: "#3f3f46", textAlign: "right" }}
                      >
                        {formatDate(row.submissionDate)}
                      </Typography>
                    </Box>
                  );
                })}

            {!isLoading && filteredRows.length === 0 && (
              <Box sx={{ p: 3, textAlign: "center" }}>
                <Typography variant="body2" color="text.secondary">
                  No farmers have taken this action yet.
                </Typography>
              </Box>
            )}
          </Card>
        </>
      )}
    </DetailDialog>
  );
}

// ─── Overview with farmer drill-down dialog ────────────────────────

export function ActionFarmersTable() {
  const [selectedAction, setSelectedAction] = useState<SelectedAction | null>(
    null
  );

  return (
    <>
      <ActionsOverviewTable onSelectAction={setSelectedAction} />
      {selectedAction && (
        <ActionFarmerBreakdown
          key={selectedAction.actionId}
          actionId={selectedAction.actionId}
          actionTitle={selectedAction.action}
          onClose={() => setSelectedAction(null)}
        />
      )}
    </>
  );
}
